import "datatables.net-bs4";
import "datatables.net-responsive-bs4";
import $ from "jquery";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { createRoot } from "react-dom/client";
import { useNavigate } from "react-router-dom";
import { useContextEmpleado } from "../../../../contexto/empleado/EmpleadoContext";
import { useContextUsuario } from "../../../../contexto/usuario/UsuarioContext";

import TablaLote from './tablas/TablaLote';

const TablaSelectLotes = forwardRef(({ datos }, ref) => {
    const { token } = useContextUsuario();
    const { setListaProductos, setListaLotesInventarios, ActualizarLista } = useContextEmpleado();
    const tableRef = useRef();
    const navigate = useNavigate();
    const rowRoots = useRef({});

    useImperativeHandle(ref, () => ({
        adjustColumns() {
            if ($.fn.DataTable.isDataTable(tableRef.current)) {
                setTimeout(() => {
                    $(tableRef.current).DataTable().columns.adjust().draw();
                }, 0);
            }
        }
    }));

    const handleEditarLoteProducto = (productId, loteId) => {
        navigate(`/app/admin/lotes/asignar/${productId}/${loteId}`);
    };

    const handleEditarLoteInventarioEspecífico = (productId, loteId, loteInventarioId) => {
        navigate(`/app/admin/lotes/asignar/${productId}/${loteId}/${loteInventarioId}`);
    };

    function format(rowData, parentDataTable) {
        const container = document.createElement('div');
        container.className = "lotes-details-container";

        const root = createRoot(container);
        root.render(
            <TablaLote
                lotes={rowData.lotes || []}
                productId={rowData.id}
                handleEditarLoteProducto={handleEditarLoteProducto}
                handleEditarLoteInventarioEspecífico={handleEditarLoteInventarioEspecífico}
                parentDataTable={parentDataTable}
            />
        );
        return container;
    }

    useEffect(() => {
        if (!datos || datos.length === 0) {
            if ($.fn.DataTable.isDataTable(tableRef.current)) {
                $(tableRef.current).DataTable().destroy();
                $(tableRef.current).empty();
            }
            return;
        }

        const requiredFields = ['id', 'nombre', 'codigo', 'lotes'];
        const missingFields = requiredFields.filter(field => !datos[0].hasOwnProperty(field));
        if (missingFields.length > 0) {
            console.error(`Los datos no contienen las propiedades necesarias: ${missingFields.join(', ')}`);
            if ($.fn.DataTable.isDataTable(tableRef.current)) {
                $(tableRef.current).DataTable().destroy();
                $(tableRef.current).empty();
            }
            return;
        }

        if ($.fn.DataTable.isDataTable(tableRef.current)) {
            $(tableRef.current).DataTable().destroy();
            $(tableRef.current).empty();
            Object.values(rowRoots.current).forEach(root => root.unmount());
            rowRoots.current = {};
        }

        const table = $(tableRef.current).DataTable({
            data: datos,
            columns: [
                {
                    targets: 0,
                    title: "Producto",
                    data: null,
                    render: (data, type, row) => {
                        return `
                            <div>
                                <strong class="text-wrap">${row.nombre}</strong><br/>
                                <small class="text-muted">Código: ${row.codigo || ""}</small>
                                <br/>
                                <small class="text-muted text-truncate" style="max-width: 200px; display: inline-block;" title="${row.descripcion || ""}">
                                    ${row.descripcion || ""}
                                </small>
                            </div>
                            <div style="display: flex; justify-content: flex-start; gap: 0.5em; flex-wrap: wrap; margin-top: 0.5em;">
                                <button
                                    key="details-${row.id}"
                                    class="btn btn-info btn-sm toggle-details"
                                    title="Ver Detalles de Lotes"
                                    style="min-width: 35px;"
                                    data-row-id="${row.id}"
                                >
                                    <i class="fas fa-eye"></i>
                                </button>
                            </div>
                        `;
                    },
                    orderable: false,
                },
                { targets: 1, title: "Cantidad Total Lotes", data: "lotes", render: (lotes) => lotes.length },
                {
                    targets: 2,
                    title: "Estado Producto",
                    data: "estado",
                    render: (data) => {
                        return `<span class="badge ${data === "Disponible" ? "badge-success" : "badge-danger"
                            }">${data}</span>`;
                    },
                },
            ],
            paging: true,
            lengthChange: false,
            ordering: true,
            info: true,
            autoWidth: true,
            responsive: true,
            searching: false,
            dom:
                "<'row mb-3'<'col-sm-12 col-md-6 text-left'l><'col-sm-12 col-md-6 text-right'f>>" +
                "<'row'<'col-sm-12'tr>>" +
                "<'row mt-3'<'col-sm-12 col-md-5'i><'col-sm-12 col-md-7'p>>",
            language: {
                decimal: "",
                emptyTable: "No hay información",
                info: "Mostrando _START_ a _END_ de _TOTAL_ Entradas",
                infoEmpty: "Mostrando 0 to 0 of 0 Entradas",
                infoFiltered: "(Filtrado de _MAX_ total entradas)",
                infoPostFix: "",
                thousands: ",",
                lengthMenu: "Mostrar _MENU_ Entradas",
                loadingRecords: "Cargando...",
                processing: "Procesando...",
                search: "Buscar:",
                zeroRecords: "Sin resultados encontrados",
                paginate: {
                    first: "|<<",
                    last: ">>|",
                    next: ">>",
                    previous: "<<",
                },
            },
            lengthMenu: [
                [5, 10, 25, 50, -1],
                [5, 10, 25, 50, "Todos"]
            ],
            stateSave: true,
            destroy: true,
        });

        $(tableRef.current).off('click', 'button.toggle-details').on('click', 'button.toggle-details', function () {
            const tr = $(this).closest('tr');
            const row = table.row(tr);

            if (row.child.isShown()) {
                row.child.hide();
                tr.removeClass('shown');
            } else {
                row.child(format(row.data(), table)).show();
                tr.addClass('shown');
            }
            setTimeout(() => { table.columns.adjust().draw(); }, 0);
        });

        return () => {
            if ($.fn.DataTable.isDataTable(tableRef.current)) {
                $(tableRef.current).DataTable().destroy();
            }
            $(tableRef.current).off('click', 'button.toggle-details');
        };
    }, [datos, token, setListaProductos, ActualizarLista, navigate]);

    return (
        <div className="table-responsive">
            <table
                ref={tableRef}
                id="productos"
                className="table table-bordered table-hover nowrap"
                style={{ width: "100%" }}
            ></table>
        </div>
    );
});

export default TablaSelectLotes;